/**
 * Leave payout drawer (#23311). Two-step "review estimate → confirm and
 * post" flow:
 *
 *   1. On open, fetches `getLeavePayoutEstimate` (server-side derives the
 *      daily rate from `monthly_salary_cents` and the CURRENT
 *      `leave_balances` rows) and `listPaymentSourceAccounts` in parallel.
 *   2. Renders the per-type estimate table with an integer-minor-unit
 *      total, plus a date input and a bank-account picker.
 *   3. On "Post payout", calls `postLeavePayout` — the mutation NEVER
 *      accepts a caller-supplied cash amount (see payroll.ts's
 *      `postLeavePayout` docblock — the amount is always re-derived
 *      server-side inside the same transaction that CAS-claims each
 *      `leave_balances` row); this drawer only supplies date +
 *      `bank_account_id`, never the amount.
 *
 * ── Read-only-grant gating
 *
 * Unreachable when the OAuth grant isn't write-capable — the
 * `LeaveBalancesPanel` above never renders the button that opens this
 * drawer, and `post_payout` is `undefined` in `useLeavePayout` for a
 * read-only grant, so this drawer is only ever opened with a valid write
 * handler in hand. Kept typed as required (not optional) to make the
 * "unreachable state" a compile-time impossibility.
 *
 * ── Money handling
 *
 * Every value flows in integer minor units (docs/money-rules.md). The
 * estimate response's `payout_cents` / `total_payout_cents` / `daily_rate_cents`
 * are integers; this drawer formats them via `format_money()` (which
 * divides by 100) exactly once at the presentation boundary. Never a
 * `parseFloat` here.
 */
import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  DateInput,
  Drawer,
  StyledSelect,
  type StyledSelectOption,
  toast,
} from '@jumpstone/ui-react';
import {
  fetchPaymentSourceAccounts,
  type ListPaymentSourceAccountsResult,
  type RealPaymentSourceAccountRow,
} from '../../lib/auth/oauth_pay_run_post_actions';
import { useOAuthEntityContext } from '../../lib/auth/oauth_entity_context';
import { format_money } from '../../lib/format';
import type {
  LeavePayoutActionFailure,
  LeavePayoutActionResult,
  LeavePayoutEstimateResult,
  LeavePayoutLoadFailure,
} from '../../data/use_leave_payout';
import {
  BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY,
  type LeavePayoutEstimate,
} from '../../data/wagepro_store_leave_balances';

const LOAD_FAILURE_KEY: Record<LeavePayoutLoadFailure, string> = {
  entitlement_required: 'employees.leave_payout.error_entitlement_required',
  forbidden: 'employees.leave_payout.error_forbidden',
  not_found: 'employees.leave_payout.error_not_found',
  not_salaried: 'employees.leave_payout.error_not_salaried',
  no_unused_balance: 'employees.leave_payout.error_no_unused_balance',
  error: 'employees.leave_payout.error_generic',
};

const ACTION_FAILURE_KEY: Record<LeavePayoutActionFailure, string> = {
  entitlement_required: 'employees.leave_payout.error_entitlement_required',
  forbidden: 'employees.leave_payout.error_forbidden',
  not_found: 'employees.leave_payout.error_not_found',
  bank_account_not_found: 'employees.leave_payout.error_bank_account_not_found',
  not_salaried: 'employees.leave_payout.error_not_salaried',
  no_unused_balance: 'employees.leave_payout.error_no_unused_balance',
  stale_balance: 'employees.leave_payout.error_stale_balance',
  period_locked: 'employees.leave_payout.error_period_locked',
  error: 'employees.leave_payout.error_generic',
};

function today_iso(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface LeavePayoutDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee_id: string;
  employee_name: string;
  currency: string;
  fetch_estimate: (options?: {
    as_of_date?: string;
  }) => Promise<LeavePayoutEstimateResult>;
  post_payout: (input: {
    date: string;
    bank_account_id: string;
  }) => Promise<LeavePayoutActionResult>;
  onSuccess: () => void;
}

export function LeavePayoutDrawer({
  open,
  onOpenChange,
  employee_id: _employee_id,
  employee_name,
  currency,
  fetch_estimate,
  post_payout,
  onSuccess,
}: LeavePayoutDrawerProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const entityContext = useOAuthEntityContext();
  const readyEntity = entityContext.status === 'ready' ? entityContext : null;

  const [estimate, set_estimate] = React.useState<LeavePayoutEstimate | null>(
    null,
  );
  const [load_failure, set_load_failure] =
    React.useState<LeavePayoutLoadFailure | null>(null);
  const [action_failure, set_action_failure] =
    React.useState<LeavePayoutActionFailure | null>(null);
  const [bank_accounts, set_bank_accounts] = React.useState<
    RealPaymentSourceAccountRow[]
  >([]);
  const [bank_accounts_loaded, set_bank_accounts_loaded] =
    React.useState(false);
  const [date, set_date] = React.useState(today_iso());
  const [bank_account_id, set_bank_account_id] = React.useState<string | null>(
    null,
  );
  const [is_submitting, set_is_submitting] = React.useState(false);
  const [phase, set_phase] = React.useState<'review' | 'confirm'>('review');
  // Bumped after a stale_balance / no_unused_balance rejection to force the
  // date-effect to refetch the estimate against fresh balances. Without
  // this, the drawer resets to `review` phase but keeps the pre-rejection
  // estimate on screen — the operator sees the STALE total and, on the very
  // next click, disburses the fresh (real) amount instead. Same shape as
  // `useLeaveRequests`'s `reloadToken`.
  const [estimate_reload_token, set_estimate_reload_token] = React.useState(0);
  // Guard for `handle_post` — the drawer's own close-effect resets
  // `action_failure`, but a post left in flight past close would resolve
  // AFTER the reset ran and leak a stale error into the next open.
  const inflight_post = React.useRef<{ cancelled: boolean } | null>(null);

  // Reset every piece of local state when the drawer closes, so a subsequent
  // open (potentially for a different employee) doesn't render stale state.
  // Also cancels any in-flight post so its resolution can't leak a stale
  // `action_failure` into the next open.
  React.useEffect(() => {
    if (!open) {
      if (inflight_post.current !== null) {
        inflight_post.current.cancelled = true;
      }
      set_estimate(null);
      set_load_failure(null);
      set_action_failure(null);
      set_bank_accounts([]);
      set_bank_accounts_loaded(false);
      set_date(today_iso());
      set_bank_account_id(null);
      set_is_submitting(false);
      set_phase('review');
    }
  }, [open]);

  // Load bank accounts once when the drawer opens — this is the pay-source
  // picker's fixed list and doesn't depend on the payout date. Split from
  // the estimate refetch below so a date change doesn't re-issue this read.
  React.useEffect(() => {
    if (!open || readyEntity === null) return;
    const box: { cancelled: boolean } = { cancelled: false };
    const isCancelled = (): boolean => box.cancelled;
    const firmId = readyEntity.firm_id;
    const entityId = readyEntity.entity_id;
    async function load(): Promise<void> {
      const bank: ListPaymentSourceAccountsResult =
        await fetchPaymentSourceAccounts({
          firm_id: firmId,
          entity_id: entityId,
        });
      if (isCancelled()) return;
      if (bank.ok) {
        set_bank_accounts(bank.accounts);
      }
      set_bank_accounts_loaded(true);
    }
    void load();
    return () => {
      box.cancelled = true;
    };
  }, [open, readyEntity]);

  // Re-fetch the estimate any time the payout date changes — the backend
  // prices the daily rate as-of that exact date (`daily_rate_cents =
  // monthly_salary_cents / days-in-that-month`, see
  // `computeLeavePayoutDailyRateCents`), and the mutation ALSO prices its
  // own daily rate as-of `input.date`. If the drawer showed an estimate
  // priced against a different `as_of_date` (say today, but the operator
  // moved the payout to a termination-month back-date), the confirmed total
  // and the posted total would silently disagree — a UX / audit disaster
  // even though the mutation itself is arithmetically correct. Wiring the
  // estimate's `as_of_date` to the drawer's `date` state closes that gap.
  React.useEffect(() => {
    if (!open) return;
    const box: { cancelled: boolean } = { cancelled: false };
    const isCancelled = (): boolean => box.cancelled;
    // Show the skeleton again while a fresh estimate is in flight.
    set_estimate(null);
    set_load_failure(null);
    async function load(): Promise<void> {
      const est = await fetch_estimate({ as_of_date: date });
      if (isCancelled()) return;
      if (est.outcome === 'failure') {
        set_load_failure(est.failure);
        return;
      }
      set_estimate(est.estimate);
    }
    void load();
    return () => {
      box.cancelled = true;
    };
  }, [open, fetch_estimate, date, estimate_reload_token]);

  const bank_options: StyledSelectOption[] = bank_accounts.map((row) => ({
    value: row.id,
    label: row.name,
  }));

  const total_cents = estimate?.total_payout_cents ?? 0;
  const has_payable = total_cents > 0;
  const can_review_submit =
    estimate !== null &&
    has_payable &&
    bank_accounts_loaded &&
    bank_account_id !== null &&
    date !== '';

  async function handle_post(): Promise<void> {
    if (bank_account_id === null) return;
    // Track this attempt so a drawer close that fires while it's in flight
    // can drop the post's own state writes on resolution (see the close
    // reset above).
    const attempt = { cancelled: false };
    inflight_post.current = attempt;
    set_is_submitting(true);
    set_action_failure(null);
    const result = await post_payout({ date, bank_account_id });
    if (attempt.cancelled) return;
    inflight_post.current = null;
    set_is_submitting(false);
    if (result.outcome === 'failure') {
      set_action_failure(result.failure);
      if (
        result.failure === 'stale_balance' ||
        result.failure === 'no_unused_balance'
      ) {
        // Balance changed under us — reset to review AND refetch the
        // estimate so the operator sees the fresh number before the next
        // click, not the stale one that just failed to post.
        set_phase('review');
        set_estimate_reload_token((token) => token + 1);
      }
      return;
    }
    toast.success(t('employees.leave_payout.success', { name: employee_name }));
    onSuccess();
  }

  return (
    <Drawer
      open={open}
      onClose={() => onOpenChange(false)}
      title={t('employees.leave_payout.drawer_title', { name: employee_name })}
    >
      <div className="flex flex-col gap-3.5 p-1">
        {estimate === null && load_failure === null && (
          <>
            <span className="sr-only">
              {t('employees.leave_payout.estimate_loading')}
            </span>
            <div className="nv-skel" style={{ height: 96 }} />
          </>
        )}

        {load_failure !== null && (
          <Alert
            variant="danger"
            title={t('employees.leave_payout.estimate_error')}
          >
            {t(LOAD_FAILURE_KEY[load_failure])}
          </Alert>
        )}

        {estimate !== null && (
          <>
            <table className="nv-table" data-testid="leave-payout-estimate">
              <thead>
                <tr>
                  <th scope="col">
                    {t('employees.leave_payout.col_leave_type')}
                  </th>
                  <th scope="col" className="text-end">
                    {t('employees.leave_payout.col_balance_days')}
                  </th>
                  <th scope="col" className="text-end">
                    {t('employees.leave_payout.col_payout')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {estimate.lines.map((line) => (
                  <tr key={line.leave_type}>
                    <td>
                      {t(BALANCE_TRACKED_LEAVE_TYPE_LABEL_KEY[line.leave_type])}
                    </td>
                    <td className="text-end num">
                      {new Intl.NumberFormat(locale, {
                        minimumFractionDigits:
                          line.balance_days % 1 === 0 ? 0 : 2,
                        maximumFractionDigits: 2,
                      }).format(line.balance_days)}
                    </td>
                    <td className="text-end num">
                      {format_money(line.payout_cents, currency, locale)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} className="text-end font-semibold">
                    {t('employees.leave_payout.total_label')}
                  </td>
                  <td
                    className="text-end font-semibold num"
                    data-testid="leave-payout-total"
                  >
                    {format_money(total_cents, currency, locale)}
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="text-[12.5px] text-[color:var(--ink-muted)]">
              {t('employees.leave_payout.daily_rate_note', {
                amount: format_money(
                  estimate.daily_rate_cents,
                  currency,
                  locale,
                ),
                as_of: estimate.as_of_date,
              })}
            </p>

            {!has_payable && (
              <Alert
                variant="warning"
                title={t('employees.leave_payout.empty_title')}
              >
                {t('employees.leave_payout.empty_description')}
              </Alert>
            )}

            {has_payable && (
              <>
                <label className="flex flex-col gap-1">
                  <span className="nv-label">
                    {t('employees.leave_payout.date_label')}
                  </span>
                  <DateInput
                    value={date}
                    onChange={(event) => set_date(event.target.value)}
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="nv-label">
                    {t('employees.leave_payout.bank_account_label')}
                  </span>
                  <StyledSelect
                    value={bank_account_id ?? ''}
                    options={bank_options}
                    placeholder={t(
                      'employees.leave_payout.bank_account_placeholder',
                    )}
                    onChange={(next) => set_bank_account_id(next || null)}
                  />
                </label>

                {action_failure !== null && (
                  <Alert
                    variant="danger"
                    title={t('employees.leave_payout.action_error_title')}
                  >
                    {t(ACTION_FAILURE_KEY[action_failure])}
                  </Alert>
                )}

                {phase === 'review' && (
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                      {t('common.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => set_phase('confirm')}
                      disabled={!can_review_submit}
                      data-testid="leave-payout-review-continue"
                    >
                      {t('employees.leave_payout.continue_cta')}
                    </Button>
                  </div>
                )}

                {phase === 'confirm' && (
                  <>
                    <Alert
                      variant="info"
                      title={t('employees.leave_payout.confirm_heading')}
                    >
                      {t('employees.leave_payout.confirm_note', {
                        amount: format_money(total_cents, currency, locale),
                        name: employee_name,
                      })}
                    </Alert>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => set_phase('review')}
                        disabled={is_submitting}
                      >
                        {t('employees.leave_payout.back_cta')}
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => {
                          void handle_post();
                        }}
                        disabled={is_submitting}
                        data-testid="leave-payout-post"
                      >
                        {is_submitting
                          ? t('employees.leave_payout.posting')
                          : t('employees.leave_payout.post_cta')}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
